<template>
  <form id="arenaUserForm" class="mx-auto col-sm-8" @submit.prevent>
    <div class="row">
      <div
        class="form-group col-lg-6"
        :class="{ 'has-error': errors.has('name') }"
      >
        <label for="name">{{ $t("name") }}</label>
        <input
          type="text"
          id="name"
          name="name"
          class="form-control"
          :placeholder="$t('type_name')"
          v-model="user.USR_NOM"
          v-validate="'required'"
        />
        <small v-show="errors.has('name')" class="form-text text-muted">
          {{ errors.first("name") }}
        </small>
      </div>

      <div
        class="form-group col-lg-6"
        :class="{ 'has-error': errors.has('login') }"
      >
        <label for="login">{{ $t("Login") }}</label>
        <input
          type="text"
          id="login"
          name="login"
          class="form-control"
          placeholder="Informe o login"
          v-model="user.USR_LGN"
          v-validate="'required'"
        />
        <small v-show="errors.has('login')" class="form-text text-muted">
          {{ errors.first("login") }}
        </small>
      </div>
    </div>

    <div class="row">
      <div class="form-group col-lg-6">
        <label for="email">{{ $t("email") }}</label>
        <input
          type="email"
          class="form-control"
          id="email"
          name="email"
          :placeholder="$t('type_email')"
          v-model="user.USR_EML"
        />
      </div>

      <div class="form-group col-lg-6">
        <label for="usertype">{{ $t("usertype") }}</label>
        <Combobox
          class="bg-white"
          name="usertype"
          :placeholder="$t('select_a_item')"
          :hasErrors="errors.has('usertype')"
          label="TUS_DSC"
          :options="userTypes"
          :initial-value="user.TUS_ID"
          v-model="user.TUS_ID"
          :reduce="(userType) => userType.id"
          v-validate="'required'"
        />

        <small v-show="errors.has('usertype')" class="form-text text-muted">
          {{ errors.first("usertype") }}
        </small>
      </div>
    </div>

    <div class="form-group">
      <label for="client">{{ $t("client") }}</label>
      <Combobox
        class="bg-white"
        name="client"
        :placeholder="$t('select_a_item')"
        :hasErrors="errors.has('client')"
        label="CLI_DSC"
        :options="clients"
        :initial-value="user.CLI_ID"
        v-model="user.CLI_ID"
        :reduce="(client) => client.CLI_ID"
        v-validate="'required'"
      />

      <small v-show="errors.has('client')" class="form-text text-muted">
        {{ errors.first("client") }}
      </small>
    </div>

    <div class="row">
      <div class="form-group col-lg-6" :class="{ 'has-error': errors.has('password') }">
        <label for="password">{{ $t("password") }}</label>
        <div class="input-group">
          <input
            :type="passwordFieldType"
            class="form-control"
            id="password"
            name="password"
            :placeholder="$t('type_password')"
            v-model="user.USR_PWD"
            v-validate="
              !this.user.id || user.USR_PWD ? 'required|min:6|max:35' : ''
            "
            ref="password"
          />

          <div class="input-group-append">
            <button
              type="button"
              class="btn btn-secondary"
              @click="switchVisibility('passwordFieldType')"
            >
              <span
                :class="[
                  passwordFieldType === 'text'
                    ? 'far fa-eye'
                    : 'far fa-eye-slash',
                ]"
              ></span>
            </button>
          </div>
        </div>

        <small v-show="errors.has('password')" class="form-text text-muted">
          {{ errors.first("password") }}
        </small>
      </div>

      <div class="form-group col-lg-6">
        <label for="datetime_expiration">{{ $t('datetime_expiration') }}</label>
        <input
          type="datetime-local"
          id="datetime_expiration"
          name="datetime_expiration"
          class="form-control"
          :placeholder="$t('ph_datetime_expiration')"
          v-model="formattedUSR_DHE"
        />
      </div>
    </div>

    <div class="row">

    </div>

    <div class="form-group row">
      <div class="col-sm-4 col-sm-offset-2">
        <LoaderButton @click="onClick">
          {{ $t("save") }}
        </LoaderButton>
      </div>
    </div>
  </form>
</template>

<script>
import { mapActions, mapState } from "vuex";
import LoaderButton from "@/components/button/LoaderButton";
import Combobox from "@/components/shared/ComboBox";
import moment from 'moment'

export default {
  name: "ArenaUserForm",
  components: {
    LoaderButton,
    Combobox,
  },

  data() {
    return {
      passwordFieldType: 'text',
      currentYear: moment().year(),
      yearsToAdd: 30,
    }
  },

  props: {
    user: {
      type: Object,
      default: () => ({
        id: undefined,
        USR_NOM: "",
        USR_LGN: "",
        USR_EML: "",
        TUS_ID: undefined,
        CLI_ID: undefined,
        USR_PWD: '',
        USR_DHE: '',
      }),
    },
  },

  computed: {
    ...mapState({
      clients: (state) => state.arena.clients,
      userTypes: (state) => state.arena.userTypes,
    }),

    formattedUSR_DHE: {
      get() {
        const value = this.user.USR_DHE;

        if (!value) {
          return moment().year(this.currentYear + this.yearsToAdd).format('YYYY-MM-DDTHH:mm');
        }

        return moment(value).format('YYYY-MM-DDTHH:mm');
      },
      set(val) {
        this.user.USR_DHE = moment(val).toISOString();
      }
    },
  },

  methods: {
    ...mapActions("arena", ["createUser", "editUser"]),

    async onClick(evt, done) {
      const valid = await this.$validator.validateAll();
      if (!valid) {
        const err = new Error(this.$t("invalid_form_fields"));

        this.$store.dispatch("notify/error", err);

        return;
      }

      const { id, USR_DHE, ...rest } = this.user;

      const data = {
        ...rest,
        USR_DHE: USR_DHE || moment().year(this.currentYear + this.yearsToAdd).format('YYYY-MM-DD HH:mm:ss'),
      }

      const result = id
        ? await this.editUser({ id, data })
        : await this.createUser(data);

      if (result) {
        this.$router.push({ name: "listarenauser" });
      }
    },

    switchVisibility (property) {
      this[property] = this[property] === 'password' ? 'text' : 'password'
    },
  },

  async mounted() {
    const { dispatch } = this.$store

    await Promise.all([
      dispatch("arena/fetchClients"),
      dispatch("arena/fetchArenaUserTypes"),
    ]);
  },
};
</script>
